import { useEffect, useRef, useReducer } from 'react'
import { useMutation } from '@tanstack/react-query'
import { ValuationApiError, tenantImportResultSchema } from '@einwert/valuation-api-client'
import type {
    AiWorkflowRequest,
    AiWorkflowResponse,
    AsyncInvokeAgentProgressSnapshot,
    TenantImportAnswer,
    TenantImportContext,
    TenantImportRow,
} from '@einwert/valuation-api-client'
import { AI_WORKFLOW_ERROR_MESSAGES } from 'ComponentLibrary/src/common/valuation-import/ai_workflow_errors'
import { getSmartPdfImportErrorMessage } from 'ComponentLibrary/src/common/valuation-import/pdf_file'
import { tenantImportAttachment } from './workbook'
import { initialTenantImportState, tenantImportReducer } from './tenantImportReducer'
import { confirmedAnswers } from './clarifications'
import { tenantImportCopy, type TenantImportLanguage } from './copy'

export type TenantImportTarget = {
    completion?: 'import' | 'download'
    loadContext: (file: File, signal: AbortSignal) => Promise<TenantImportContext>
    complete: (rows: TenantImportRow[]) => Promise<void>
}

export type TenantImportWorkflowInvoker = (
    request: AiWorkflowRequest,
    options: {
        signal: AbortSignal
        onProgress: (progress: AsyncInvokeAgentProgressSnapshot) => void
    }
) => Promise<AiWorkflowResponse>

type TenantImportOptions = {
    target?: TenantImportTarget
    language?: TenantImportLanguage
    invokeWorkflow: TenantImportWorkflowInvoker
}

const ERROR_MESSAGES: Record<string, string> = {
    ...AI_WORKFLOW_ERROR_MESSAGES,
    model_context_window_exceeded: 'Die Datei überschreitet die Kontextgröße des KI-Modells. Bitte nur die relevanten Tabellen hochladen.',
    model_output_max_tokens: 'Die KI konnte das Ergebnis nicht vollständig ausgeben. Bitte einen kleineren Ausschnitt hochladen.',
    document_unreadable: 'Die Datei konnte nicht gelesen werden. Bitte prüfen Sie, ob sie beschädigt oder passwortgeschützt ist.',
    tenant_import_source_invalid: 'Die Datei konnte nicht vollständig gelesen werden. Bitte die Quelldatei prüfen.',
    tenant_import_no_tenant_records:
        'Für das gewählte Objekt wurden keine einzelnen Mieter gefunden. Bitte eine Mieterliste mit Einzelmietern hochladen.',
    tenant_import_output_invalid:
        'Die Tabellen konnten nicht zuverlässig zugeordnet werden. Bitte die Datei prüfen oder einen kleineren Ausschnitt hochladen.',
    tenant_import_source_changed: 'Die Quelldatei hat sich geändert. Bitte den Import neu starten.',
}

const EN_ERROR_MESSAGES: Record<string, string> = {
    model_provider_unavailable: 'The AI service is temporarily unavailable. Please try again later.',
    async_invocation_timed_out: 'Processing took too long. Please restart the import.',
    async_invocation_interrupted: 'Processing was interrupted. Please restart the import.',
    model_context_window_exceeded: 'The file exceeds the AI model’s context limit. Please upload only the relevant tables.',
    model_output_max_tokens: 'The AI could not return the complete result. Please upload a smaller extract.',
    document_unreadable: 'The file could not be read. Please check whether it is damaged or password protected.',
    tenant_import_source_invalid: 'The file could not be fully read. Please check the source file.',
    tenant_import_no_tenant_records: 'No individual tenants were found for this property. Please upload a detailed tenant list.',
    tenant_import_output_invalid: 'The tables could not be reliably mapped. Please check the file or upload a smaller extract.',
    tenant_import_source_changed: 'The source file has changed. Please restart the import.',
    pdf_source_too_large: 'The PDF exceeds 20 MB. Please compress it and upload it again.',
    pdf_page_limit_exceeded: 'The PDF has too many pages. Please upload a shorter PDF.',
    pdf_provider_fit_failed: 'The PDF could not be prepared for AI processing. Please compress it and try again.',
}

export function useTenantImport({ target, invokeWorkflow, language = 'de' }: TenantImportOptions) {
    const copy = tenantImportCopy(language)
    const completion = target?.completion ?? 'import'
    const abort = useRef<AbortController | null>(null)
    const original = useRef<{ file: File; attachment: Awaited<ReturnType<typeof tenantImportAttachment>> } | undefined>(undefined)
    const completionAttempted = useRef(false)
    const [state, dispatch] = useReducer(tenantImportReducer, initialTenantImportState)
    const { mutateAsync: extract } = useMutation({
        mutationFn: ({ request, signal }: { request: AiWorkflowRequest; signal: AbortSignal }) =>
            invokeWorkflow(request, {
                signal,
                onProgress: (progress) => {
                    if (!signal.aborted) {
                        dispatch({ type: 'progress', progress })
                    }
                },
            }),
    })
    const { step, file, progress, result, error, importedCount, answers } = state
    const saving = state.saveStatus === 'saving'
    const completionFailed = state.saveStatus === 'failed' && completion === 'import'
    const setFile = (file?: File) => dispatch({ type: 'select_file', file })
    const setAnswers = (answers: TenantImportAnswer[]) => dispatch({ type: 'answer', answers })
    const rows = result?.rows ?? []
    const canComplete = !!result?.ready

    useEffect(() => () => abort.current?.abort(), [])

    const cancel = () => {
        abort.current?.abort()
    }

    const runAnalysis = async (source: typeof state, currentAnswers: TenantImportAnswer[]) => {
        const { file, result, confirmed, clarificationRound } = source
        if (!file || !target || abort.current) {
            return
        }
        const controller = new AbortController()
        abort.current = controller
        dispatch({ type: 'start_analysis' })
        completionAttempted.current = false
        const selectedAnswers = currentAnswers.filter((answer) => answer.selected_option_ids.length || answer.text.trim())
        const decisions = confirmedAnswers(confirmed, selectedAnswers)
        try {
            const [attachment, context] = await Promise.all([
                original.current?.file === file ? original.current.attachment : tenantImportAttachment(file),
                target.loadContext(file, controller.signal),
            ])
            controller.signal.throwIfAborted()
            original.current = { file, attachment }
            const input = {
                ...context,
                language,
                clarifications: decisions,
                clarification_round: clarificationRound + (selectedAnswers.length ? 1 : 0),
                source_fingerprint: result?.source_fingerprint ?? '',
            }
            const response = await extract({
                signal: controller.signal,
                request: {
                    prompt: `einwert-ai-workflow:v1\n${JSON.stringify({ workflow: 'tenant_import', operation: 'extract', input })}`,
                    files: [attachment],
                },
            })
            controller.signal.throwIfAborted()
            const extracted = tenantImportResultSchema.parse(JSON.parse(response.response))
            dispatch({
                type: 'complete_analysis',
                result: extracted,
                confirmed: decisions,
                round: clarificationRound + (selectedAnswers.length ? 1 : 0),
            })
        } catch (cause) {
            if (controller.signal.aborted) {
                return
            }
            const message =
                cause instanceof ValuationApiError
                    ? (cause.responseErrorMessage ?? cause.message)
                    : cause instanceof Error
                      ? cause.message
                      : copy.analysisFailed
            let error = message
            if (language === 'de') {
                error = ERROR_MESSAGES[message] ?? getSmartPdfImportErrorMessage(new Error(message), message)
            } else {
                error = EN_ERROR_MESSAGES[message] ?? copy.analysisFailed
            }
            dispatch({ type: 'fail_analysis', error })
        } finally {
            abort.current = null
        }
    }

    const analyze = (currentAnswers = answers) => runAnalysis(state, currentAnswers)

    const upload = async (file: File) => {
        if (!target || abort.current || saving) {
            return
        }
        setFile(file)
        await runAnalysis({ ...initialTenantImportState, file }, [])
    }

    const complete = async () => {
        if (!target || !canComplete || saving || (completion === 'import' && completionAttempted.current)) {
            return
        }
        completionAttempted.current = true
        dispatch({ type: 'start_save' })
        try {
            await target.complete(rows)
            dispatch({ type: 'complete_save', count: rows.length })
        } catch (cause) {
            dispatch({
                type: 'fail_save',
                error:
                    completion === 'download'
                        ? copy.downloadFailed
                        : `${cause instanceof Error ? cause.message : copy.saveFailed} ${copy.checkBeforeRetry}`,
            })
        }
    }

    return {
        copy,
        language,
        step,
        file,
        setFile,
        progress,
        result,
        rows,
        error,
        saving,
        completionFailed,
        importedCount,
        answers,
        setAnswers,
        canComplete,
        analyze,
        upload,
        cancel,
        complete,
    }
}

export type TenantImportFlow = ReturnType<typeof useTenantImport>
